import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { useScheduleWeeks } from "../../hooks/useScheduleWeeks";
import type { WeekData } from "../../utils/models";
import { dayIndexOfDate, weekIndexOfDate } from "../../utils/panelWeeks";
import { toIsoDate } from "../../utils/scheduleWeeks";
import { type LoadedDay, resolveLoadedDay } from "./addStaffUtils";

type Args = {
	/** The day the caller opened the panel on. */
	initialDate: DateTime;
	/** Whether this panel may move to another date at all (not for a request's locked shift, hospital or actuals). */
	enabled: boolean;
	/** Keeps the caller in step with the picked date (Staffing Changes' other tabs follow it). */
	onSelectedDateChange: ((date: DateTime) => void) | undefined;
	/** The panel the date moved in, as the `source` of its analytics and error reports (Add Staff, Remove Staff). */
	analyticsSource: string;
};

/** The day a panel shows, the weeks around it, and where the day sits in them. */
export type ShownDay = LoadedDay & { date: DateTime; weeks: WeekData[] };

export type AddStaffDate = {
	/** Undefined until the first day's weeks arrive, when the opening day has no staffing row, and while a load has failed. */
	shown: ShownDay | undefined;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
	canChangeDate: boolean;
	/** The pickable range: the loaded weeks with the schedule cache off, the whole schedule with it on. */
	minDate: DateTime | undefined;
	maxDate: DateTime | undefined;
	changeDate: (date: DateTime) => void;
};

const loadedDayIn = (weeks: WeekData[], date: DateTime): LoadedDay | null =>
	resolveLoadedDay(date, { getWeekIndexByDate: (day) => weekIndexOfDate(weeks, day), getDayIndexByDate: (day) => dayIndexOfDate(weeks, day) });

/**
 * The day a Staffing Changes panel acts on. Starts on the caller's day; the header's date picker re-targets the
 * whole tab (Add Staff: cards, list, week strip, costs; Remove Staff: its list) to another day. While the picked
 * day's weeks load, the panel keeps showing the day it was on, so its picks survive the move.
 */
export const useAddStaffDate = ({ initialDate, enabled, onSelectedDateChange, analyticsSource }: Args): AddStaffDate => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const showErrorSnackbar = useErrorSnackbar();
	const [requestedDate, setRequestedDate] = useState(initialDate);
	const schedule = useScheduleWeeks(requestedDate);
	const isReady = !schedule.isLoading && !schedule.isError;
	// Null once loaded when the day has no staffing row, which the panel can't act on.
	const requested = useMemo((): ShownDay | null | undefined => {
		if (!isReady) return undefined;
		const day = loadedDayIn(schedule.weeks, requestedDate);
		return day && { ...day, date: requestedDate, weeks: schedule.weeks };
	}, [isReady, schedule.weeks, requestedDate]);
	const [lastShown, setLastShown] = useState<ShownDay | undefined>(undefined);

	useEffect(() => {
		if (requested) setLastShown(requested);
	}, [requested]);

	useEffect(() => {
		if (requested !== null || !lastShown) return;
		showErrorSnackbar(t("add.staff.date.not.loaded"), { source: analyticsSource, date: requestedDate.toISODate() });
		setRequestedDate(lastShown.date);
		onSelectedDateChange?.(lastShown.date);
	}, [requested, lastShown, showErrorSnackbar, t, analyticsSource, requestedDate, onSelectedDateChange]);

	// While the picked day loads, or when it has no staffing row, the day already on screen stays. A failed load shows
	// its error and Retry instead, so the panel never acts on a day other than the one picked.
	const shown = schedule.isError ? undefined : (requested ?? lastShown);

	const changeDate = useCallback(
		(date: DateTime) => {
			if (!shown || toIsoDate(date) === toIsoDate(shown.date)) return;
			setRequestedDate(date);
			track("Staffing - change date", { source: analyticsSource, from_date: shown.date.toISODate(), to_date: date.toISODate() });
			onSelectedDateChange?.(date);
		},
		[shown, track, analyticsSource, onSelectedDateChange],
	);

	return {
		shown,
		isLoading: schedule.isLoading,
		isError: schedule.isError,
		refetch: schedule.refetch,
		canChangeDate: enabled && !!schedule.firstDate && !!schedule.lastDate,
		minDate: schedule.firstDate,
		maxDate: schedule.lastDate,
		changeDate,
	};
};
